import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Eye, EyeSlash, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { PasswordStrength } from '../components/PasswordStrength';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * `/reset-password?token=...` — the ONLY entry point to setting a new
 * password. Mounted from the email-link only.
 *
 * Flow:
 *  1. Read `token` from the URL. If missing → show "invalid link".
 *  2. Call `/auth/reset-password/validate?token=...`. If the token is
 *     unknown / expired / already used → show a clear error and a
 *     "request a new link" CTA. Never show the form in that case.
 *  3. If valid → render the form. Submitting POSTs to
 *     `/auth/reset-password`. On 200 → success view + back-to-login.
 */
export const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();

  const [phase, setPhase] = useState('validating'); // validating | valid | invalid | done
  const [invalidReason, setInvalidReason] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setInvalidReason('missing');
      return;
    }
    let alive = true;
    axios.get(`${API}/api/auth/reset-password/validate`, { params: { token } })
      .then(({ data }) => {
        if (!alive) return;
        if (data?.valid) {
          setPhase('valid');
        } else {
          setPhase('invalid');
          setInvalidReason(data?.reason || 'invalid');
        }
      })
      .catch(() => {
        if (!alive) return;
        setPhase('invalid');
        setInvalidReason('network');
      });
    return () => { alive = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t('auth.passwordTooShort') || 'Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error(t('auth.passwordMismatch')); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setPhase('done');
      toast.success(t('auth.passwordResetSuccess'));
    } catch (err) {
      const detail = err.response?.data?.detail || 'Reset failed';
      toast.error(detail);
      // If the backend now says the token is invalid (e.g. consumed by
      // a parallel tab), surface that immediately so the UI doesn't sit
      // in a permanent submitting state.
      if (/invalid|expired/i.test(detail)) {
        setPhase('invalid');
        setInvalidReason('expired');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex flex-col items-center" data-testid="reset-logo">
            <img src="/ato-logo.png" alt="ATÔ" className="w-[160px] md:w-[200px] h-auto object-contain" />
          </Link>
        </div>
        {children}
      </div>
    </div>
  );

  if (phase === 'validating') {
    return (
      <Shell>
        <div className="bg-white border border-border rounded-2xl p-8 shadow-sm text-center" data-testid="reset-validating">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text_muted">{t('common.loading')}</p>
        </div>
      </Shell>
    );
  }

  if (phase === 'invalid') {
    const messages = {
      missing: t('auth.resetTokenMissing') || 'Aucun token fourni — ouvrez le lien depuis votre email.',
      invalid: t('auth.resetTokenInvalid') || 'Ce lien de réinitialisation est invalide.',
      used:    t('auth.resetTokenUsed') || 'Ce lien a déjà été utilisé. Demandez-en un nouveau.',
      expired: t('auth.resetTokenExpired') || 'Ce lien a expiré. Demandez-en un nouveau.',
      network: t('auth.resetTokenNetwork') || 'Impossible de vérifier le lien. Réessayez plus tard.',
    };
    return (
      <Shell>
        <div className="bg-white border border-border rounded-2xl p-8 shadow-sm text-center" data-testid="reset-invalid">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-danger-soft mb-4">
            <WarningCircle size={26} weight="fill" className="text-danger" />
          </div>
          <h2 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-2">
            {t('auth.linkUnusable') || 'Lien inutilisable'}
          </h2>
          <p className="text-text_muted mb-6" data-testid="reset-invalid-reason">
            {messages[invalidReason] || messages.invalid}
          </p>
          <Link to="/forgot-password">
            <Button variant="primary" className="w-full py-3 font-semibold" data-testid="request-new-link-btn">
              {t('auth.requestNewLink') || 'Demander un nouveau lien'}
            </Button>
          </Link>
          <p className="text-center text-sm text-text_muted mt-4">
            <Link to="/login" className="text-primary hover:underline font-medium">{t('auth.backToLogin')}</Link>
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === 'done') {
    return (
      <Shell>
        <div className="bg-white border border-border rounded-2xl p-8 shadow-sm text-center" data-testid="reset-done">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-success-soft mb-4">
            <CheckCircle size={26} weight="fill" className="text-success" />
          </div>
          <h2 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-3">
            {t('auth.passwordResetSuccess')}
          </h2>
          <p className="text-text_muted mb-6">{t('auth.canLoginNow')}</p>
          <Link to="/login">
            <Button variant="primary" className="w-full py-3 font-semibold" data-testid="back-to-login-after-reset">
              {t('auth.signIn')}
            </Button>
          </Link>
        </div>
      </Shell>
    );
  }

  // phase === 'valid'
  return (
    <Shell>
      <div className="bg-white border border-border rounded-2xl p-8 shadow-sm" data-testid="reset-form-card">
        <h2 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-2" data-testid="reset-title">
          {t('auth.setNewPassword')}
        </h2>
        <p className="text-text_muted mb-6">{t('auth.enterNewPassword')}</p>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="newPassword">{t('auth.newPassword')}</Label>
            <div className="relative">
              <Input
                id="newPassword" type={showPw ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} required minLength={6}
                className="border border-border pr-11" autoComplete="new-password"
                data-testid="new-password-input"
              />
              <button
                type="button" onClick={() => setShowPw((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-text_muted hover:text-text_main transition-colors"
                aria-label={showPw ? 'Hide password' : 'Show password'}
                data-testid="new-password-toggle-button"
              >
                {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <PasswordStrength password={password} />
          </div>
          <div>
            <Label htmlFor="confirmNewPassword">{t('auth.confirmPassword')}</Label>
            <div className="relative">
              <Input
                id="confirmNewPassword" type={showConfirm ? 'text' : 'password'} value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required minLength={6}
                className="border border-border pr-11" autoComplete="new-password"
                data-testid="confirm-new-password-input"
              />
              <button
                type="button" onClick={() => setShowConfirm((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-text_muted hover:text-text_main transition-colors"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                data-testid="confirm-new-password-toggle-button"
              >
                {showConfirm ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <Button
            type="submit" disabled={submitting} variant="primary"
            className="w-full py-3 font-semibold"
            data-testid="reset-submit-button"
          >
            {submitting ? t('common.loading') : t('auth.resetPassword')}
          </Button>
        </form>
      </div>
    </Shell>
  );
};
